// The chat box an ask is answered in: typed, picked from the quick replies,
// or attached as a document; or handed to someone from the directory with a
// due date; or skipped on the stated assumption. The intake asks with it,
// and the project page answers open asks with it.

import { FC, RefObject, useEffect, useRef, useState } from "react";
import { ArrowUpOutlined, CloseOutlined, PaperClipOutlined, UserAddOutlined } from "@ant-design/icons";
import { clsx } from "clsx";
import { Tooltip } from "../../../../design-system";
import { PEOPLE } from "../model/fixtures";
import { int, shortDate } from "../model/format";
import type { Fulfilment, Need } from "../model/types";
import classes from "./AskChat.module.less";

/** A need InfinityLoop fulfils itself: stated, never asked. */
export const isPulled = (need: Need) => need.suggest.mode === "pull";

export const lowerFirst = (text: string) => text.replace(/^./, (c) => c.toLowerCase());

/** What a route reads as: in the user's bubble, in the rail, on the project. */
export const said = (need: Need, how: Fulfilment): string => {
    switch (how.mode) {
        case "answer":
            return need.answer.kind === "count" ? `${how.value} of ${need.answer.total} can go` : String(how.value);
        case "delegate":
            return `Delegated to ${how.to}, due ${shortDate(how.due)}`;
        case "pull":
            return `We pull it; ${lowerFirst(need.pullable ?? "")}`;
        case "waive":
            return `Skipped: ${lowerFirst(need.waived)}`;
    }
};

/** Closes a popover on a click anywhere outside it. */
export const useClickOutside = (ref: RefObject<HTMLElement>, onClose: () => void) => {
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) onClose();
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [ref, onClose]);
};

/** The directory, searched by name or role; picking a person is the choice. */
export const PersonPicker: FC<{ value?: string; onPick: (name: string) => void }> = ({ value, onPick }) => {
    const [query, setQuery] = useState("");
    const matches = PEOPLE.filter((p) => `${p.name} ${p.role}`.toLowerCase().includes(query.toLowerCase()));
    return (
        <div>
            <input
                className={classes.popSearch}
                placeholder="Search the directory"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
            />
            <div className={classes.popList}>
                {matches.map((p) => (
                    <button
                        key={p.name}
                        type="button"
                        className={clsx(classes.popOption, p.name === value && classes.popOptionActive)}
                        onClick={() => onPick(p.name)}
                    >
                        <span>{p.name}</span>
                        <span className={classes.popRole}>{p.role}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

/** Who to hand the ask to, and by when; also edits an ask already handed out. */
export const DelegatePopover: FC<{
    need: Need;
    /** The current assignment when editing one; the need's suggestion otherwise. */
    initial?: { to: string; due: string };
    confirmLabel?: string;
    className?: string;
    onDelegate: (to: string, due: string) => void;
    onClose: () => void;
}> = ({ need, initial, confirmLabel = "Delegate", className, onDelegate, onClose }) => {
    const suggested = initial ?? (need.suggest.mode === "delegate" ? need.suggest : undefined);
    const [to, setTo] = useState(suggested?.to ?? "");
    const [due, setDue] = useState(suggested?.due ?? need.due);
    const ref = useRef<HTMLDivElement>(null);
    useClickOutside(ref, onClose);
    return (
        <div className={clsx(classes.delegatePop, className)} ref={ref}>
            <div>
                <div className={classes.popLabel}>Who</div>
                <PersonPicker value={to} onPick={setTo} />
            </div>
            <div>
                <div className={classes.popLabel}>Due</div>
                <input className={classes.dateInput} type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </div>
            <div className={classes.popFooter}>
                <button type="button" className={classes.primaryBtn} disabled={!to || !due} onClick={() => onDelegate(to, due)}>
                    {confirmLabel}
                </button>
            </div>
        </div>
    );
};

export const ChatBox: FC<{
    need: Need;
    onCommit: (how: Fulfilment) => void;
    /** Skipping makes no sense for a decision; the card hides it. */
    allowSkip?: boolean;
    /** The vendor's answer or the signed agreement is the user's own to give. */
    allowDelegate?: boolean;
    placeholder?: string;
    className?: string;
}> = ({ need, onCommit, allowSkip = true, allowDelegate = true, placeholder, className }) => {
    const [text, setText] = useState("");
    const [file, setFile] = useState<string | null>(null);
    const [delegating, setDelegating] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);
    const { answer } = need;

    const quickReplies: Array<{ label: string; value: string | number }> =
        answer.kind === "choice"
            ? answer.options.map((o) => ({ label: o, value: o }))
            : answer.kind === "count"
            ? [
                  { label: `${int(answer.suggested)} of ${int(answer.total)} can go`, value: answer.suggested },
                  { label: `All ${int(answer.total)}`, value: answer.total },
              ]
            : [];

    const send = () => {
        if (file) onCommit({ mode: "answer", value: file });
        else if (answer.kind === "count" && text.trim() !== "") onCommit({ mode: "answer", value: Number(text) });
        else if (text.trim() !== "") onCommit({ mode: "answer", value: text.trim() });
    };
    const canSend = file !== null || text.trim() !== "";

    return (
        <div className={clsx(classes.chatArea, className)}>
            {quickReplies.length > 0 && (
                <div className={clsx(classes.quickReplies, answer.kind === "choice" && classes.quickRepliesStacked)}>
                    {quickReplies.map((q, i) => (
                        <button
                            key={q.label}
                            type="button"
                            className={classes.choice}
                            onClick={() => onCommit({ mode: "answer", value: q.value })}
                        >
                            {answer.kind === "choice" && <span className={classes.choiceNumber}>{i + 1}</span>}
                            {q.label}
                        </button>
                    ))}
                </div>
            )}
            <div className={classes.chatBox}>
                {file && (
                    <span className={classes.attachment}>
                        <PaperClipOutlined /> {file}
                        <button type="button" aria-label="Remove" onClick={() => setFile(null)}>
                            <CloseOutlined />
                        </button>
                    </span>
                )}
                <textarea
                    className={classes.chatInput}
                    rows={2}
                    placeholder={
                        placeholder ??
                        (answer.kind === "count"
                            ? "Type a number, pick a quick reply, or hand it to someone"
                            : answer.kind === "document"
                            ? "Attach the file, or hand it to someone who has it"
                            : "Type your answer, pick a quick reply, or hand it to someone")
                    }
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                />
                <div className={classes.chatTools}>
                    <input
                        ref={fileInput}
                        type="file"
                        style={{ display: "none" }}
                        onChange={(e) => {
                            const picked = e.target.files?.[0];
                            if (picked) setFile(picked.name);
                            e.target.value = "";
                        }}
                    />
                    <button type="button" className={classes.toolBtn} onClick={() => fileInput.current?.click()}>
                        <PaperClipOutlined /> Attach
                    </button>
                    {allowDelegate && (
                        <button
                            type="button"
                            className={clsx(classes.toolBtn, delegating && classes.toolBtnActive)}
                            onClick={() => setDelegating((d) => !d)}
                        >
                            <UserAddOutlined /> Delegate
                        </button>
                    )}
                    {allowSkip && (
                        <Tooltip title={`${need.waived}.`} mouseEnterDelay={0.3}>
                            <button type="button" className={classes.toolBtn} onClick={() => onCommit({ mode: "waive" })}>
                                Skip
                            </button>
                        </Tooltip>
                    )}
                    <span className={classes.toolSpacer} />
                    <button type="button" className={classes.sendBtn} aria-label="Send" disabled={!canSend} onClick={send}>
                        <ArrowUpOutlined />
                    </button>
                </div>
                {delegating && (
                    <DelegatePopover
                        need={need}
                        onDelegate={(to, due) => {
                            setDelegating(false);
                            onCommit({ mode: "delegate", to, due });
                        }}
                        onClose={() => setDelegating(false)}
                    />
                )}
            </div>
        </div>
    );
};
